import PageHeader from "@/components/PageHeader";
import { CtaBanner } from "@/components/CTA";
import { Section } from "@/pages/Home";
import Seo from "@/components/Seo";
import { Anchor, Building2, GraduationCap, HeartHandshake, Trophy, Award, Linkedin } from "lucide-react";

const FOUNDER_IMG = "/assets/founder-marlon-ceo.png"; // fallback; WebP variants served via <picture>
const FOUNDER_LINKEDIN = "https://www.linkedin.com/in/marlonlferguson/";

export default function Founder() {
  return (
    <div data-testid="founder-page">
      <Seo
        title="Meet Our Founder · Marlon L. Ferguson, Founder & CEO | WHO — Warm Hand Off"
        description="Marlon L. Ferguson is the Founder & Chief Executive Officer of Warm Hand Off LLC (WHO) — a servant leader and U.S. Navy veteran working to close the gap between a referral made and a connection achieved."
        path="/founder"
      />
      <PageHeader
        eyebrow="Meet Our Founder"
        title="Marlon L. Ferguson"
        lede="Founder & Chief Executive Officer, Warm Hand Off LLC — Servant Leader | Inquisitive Problem-Solver | Lifelong Learner | Founder & CEO"
      />

      {/* Bio + Origin */}
      <section className="py-16 lg:py-24 border-t border-borderSubtle bg-white" data-testid="founder-bio-section">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-24 self-start">
            <div className="relative">
              <div className="rounded-lg overflow-hidden border border-borderSubtle bg-cream-panel">
                <picture>
                  <source
                    type="image/webp"
                    srcSet="/assets/founder-marlon-ceo-640.webp 640w, /assets/founder-marlon-ceo.webp 1023w"
                    sizes="(max-width: 1024px) 100vw, 40vw"
                  />
                  <img
                    src={FOUNDER_IMG}
                    width="1023"
                    height="1537"
                    alt="Marlon L. Ferguson, Founder and Chief Executive Officer of Warm Hand Off LLC."
                    className="w-full h-auto max-h-[620px] object-contain mx-auto block"
                    data-testid="founder-image"
                    loading="lazy"
                  />
                </picture>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-navy-900 text-white rounded-md p-5 hidden md:block max-w-[240px]">
                <p className="eyebrow text-gold">Founder & CEO</p>
                <p className="mt-1 font-display font-bold text-lg">Marlon L. Ferguson</p>
                <p className="text-white/70 text-sm mt-1">Warm Hand Off LLC</p>
              </div>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4" data-testid="founder-chips">
              <Chip icon={<Anchor className="w-4 h-4" />} label="U.S. Navy Veteran" />
              <Chip icon={<HeartHandshake className="w-4 h-4" />} label="Nonprofit Executive" />
              <Chip icon={<Building2 className="w-4 h-4" />} label="Public-sector leader" />
              <Chip icon={<GraduationCap className="w-4 h-4" />} label="Higher ed & Gov affairs" />
              <Chip icon={<Trophy className="w-4 h-4" />} label="Former Pitt Basketball" />
              <Chip icon={<Award className="w-4 h-4" />} label="All-Navy Basketball" />
            </div>
            <a
              href={FOUNDER_LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center justify-center gap-2 w-full px-4 py-3 bg-navy hover:bg-navy-deep text-cream font-semibold rounded-md text-sm transition-colors"
              data-testid="founder-linkedin-link"
            >
              <Linkedin className="w-4 h-4" /> Connect with Marlon on LinkedIn
            </a>
          </div>

          <div className="lg:col-span-7 lg:pl-6">
            <p className="eyebrow text-gold">Official biography</p>
            <h2 className="mt-4 font-display font-black text-navy-900 tracking-tight leading-[1.05] text-3xl lg:text-4xl">
              Servant leadership, measured in outcomes.
            </h2>
            <div className="mt-8 space-y-6 text-lg text-slateSoft leading-relaxed" data-testid="founder-bio-copy">
              <p>Some people treat leadership as a title. Marlon L. Ferguson treats it as a calling.</p>
              <p>
                He wears many hats, and he wears each one with purpose. He is a servant leader who measures
                success by the people he lifts up, an inquisitive problem-solver who asks the questions
                others overlook, a lifelong learner who sees every chapter as an opportunity for growth,
                and a mission-driven executive who never loses sight of why the work matters.
              </p>
              <p>
                His journey has taken him from military service to the halls of government, from university
                campuses to the front lines of nonprofit leadership, and into the challenges and
                opportunities of entrepreneurship. Through it all, the same qualities have remained
                constant: a restless curiosity, an instinct for understanding how complex systems fit
                together, and a conviction that leadership is both a responsibility and an opportunity to
                create lasting change.
              </p>
              <p>
                His standard is simple: serve first, own the outcome, build together, and leave every
                organization better than he found it.
              </p>
              <p>
                That philosophy now guides his work as Founder and Chief Executive Officer of Warm Hand Off
                LLC (WHO), where he is addressing a challenge deeply rooted in his experience serving
                veterans: ensuring that veterans and their families can navigate complex systems, access the
                services and resources they have earned, and establish meaningful connections without falling
                through the cracks.
              </p>
              <p>
                Through WHO, Ferguson is working to bridge the gap between a referral made and a connection
                achieved. His approach combines genuine human connection with responsible artificial
                intelligence, thoughtful innovation, and measurable accountability. He believes that success
                should be measured not simply by promises made or activities completed, but by meaningful
                outcomes and the lives positively affected.
              </p>
              <p>
                Ferguson has never stopped learning. He is pursuing graduate studies at the University of
                Pennsylvania and has completed the Massachusetts Institute of Technology (MIT) Computer
                Science and Artificial Intelligence Laboratory (CSAIL) Agentic AI Design Intensive. He
                approaches every new learning opportunity with an open mind and a determination to turn
                knowledge into practical solutions for the complex human challenges that matter most.
              </p>
              <p>
                At the heart of his work is an enduring belief that leadership is not defined by titles,
                positions, or authority, but by service, accountability, collaboration, and the lasting
                difference one makes in the lives of others.
              </p>
              <p>His guiding philosophy says it all:</p>
            </div>

            <div className="mt-10 p-8 bg-cream border-l-4 border-gold rounded-md" data-testid="founder-quote">
              <p className="font-display font-bold text-navy-900 text-2xl leading-snug">
                &ldquo;In Service of Good Leadership.&rdquo;
              </p>
              <p className="mt-4 text-slateSoft">— Marlon L. Ferguson, Founder & Chief Executive Officer, Warm Hand Off LLC</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service — USS Elliot ceremony */}
      <section
        className="relative py-16 lg:py-24 bg-white border-t border-borderSubtle overflow-hidden"
        data-testid="founder-service-section"
        id="navy-service"
      >
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <figure
              className="lg:col-span-8 rounded-2xl overflow-hidden border border-borderSubtle shadow-xl bg-navy-deep"
              data-testid="service-figure"
            >
              <img
                src="/assets/founder-uss-elliot-ceremony.png"
                alt="Marlon L. Ferguson in a U.S. Navy jacket bearing his nameplate 'Ferguson' shakes hands with a superior officer at a shipboard ceremony aboard the USS Elliot (DD 967). Ranks of fellow sailors stand at attention in the background with the open ocean behind them."
                className="block w-full h-auto"
                loading="lazy"
                data-testid="service-image"
              />
            </figure>

            <div className="lg:col-span-4" data-testid="service-copy">
              <p className="eyebrow text-gold-deep">Service</p>
              <h2 className="mt-3 font-display font-black text-navy tracking-tight leading-[1.05] text-3xl lg:text-4xl">
                Aboard the <span className="text-gold-deep">USS Elliot</span>.
              </h2>
              <div className="mt-4 h-[3px] w-14 bg-gold rounded-full" aria-hidden />
              <p className="mt-6 text-lg text-slateSoft leading-relaxed">
                Recognition aboard the destroyer{" "}
                <span className="text-navy font-semibold">USS Elliot (DD 967)</span>. Marlon&rsquo;s
                Navy service is the foundation of everything WHO stands for &mdash; discipline,
                duty, and the conviction that no shipmate gets left behind.
              </p>

              <blockquote
                className="mt-8 relative pl-5 border-l-4 border-gold"
                data-testid="service-pullquote"
              >
                <p className="font-display font-bold text-navy text-xl lg:text-2xl leading-[1.25]">
                  &ldquo;Service does not end when the uniform comes off.&rdquo;
                </p>
                <footer className="mt-3 text-xs uppercase tracking-[0.22em] text-navy/60 font-semibold">
                  &mdash; Marlon L. Ferguson
                </footer>
              </blockquote>

              <p className="mt-6 text-sm uppercase tracking-[0.22em] text-navy/60 font-semibold">
                U.S. Navy · Veteran
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Through Teamwork — consolidated collage + narrative */}
      <section
        className="relative py-20 lg:py-28 bg-cream-panel border-t border-borderSubtle overflow-hidden"
        data-testid="founder-athletics-section"
        id="leadership-through-teamwork"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.04] blur-3xl" />
        </div>

        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-[820px]">
            <p className="eyebrow text-gold-deep" data-testid="teamwork-eyebrow">
              Formative years
            </p>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl">
              Leadership Through <span className="text-gold-deep">Teamwork</span>.
            </h2>
            <div className="mt-5 h-[3px] w-16 bg-gold rounded-full" aria-hidden />
          </div>

          <figure
            className="mt-10 rounded-2xl overflow-hidden border border-borderSubtle shadow-xl bg-navy-deep"
            data-testid="teamwork-collage-figure"
          >
            <img
              src="/assets/founder-leadership-collage.png"
              alt="Three-photo collage of Marlon L. Ferguson's basketball career: a University of Pittsburgh #15 game action shot, the All-Navy team photographed in front of a U.S. Navy jet with the San Francisco skyline, and the Big East Conference Summer Traveling Team photographed on the Boston College court."
              className="block w-full h-auto"
              loading="lazy"
              data-testid="teamwork-collage-image"
            />
          </figure>

          <div
            className="mt-12 grid lg:grid-cols-12 gap-10 max-w-[1180px]"
            data-testid="teamwork-narrative"
          >
            <div className="lg:col-span-8 space-y-6 text-lg text-slateSoft leading-[1.75]">
              <p>
                Three chapters. One lesson. Marlon competed as a{" "}
                <span className="text-navy font-semibold">Division I student-athlete at the
                University of Pittsburgh</span>, earned a spot on the{" "}
                <span className="text-navy font-semibold">Big East Conference Summer Traveling
                Team</span> alongside some of the nation&rsquo;s premier collegiate players, and
                represented the country on the{" "}
                <span className="text-navy font-semibold">U.S. Navy All-Navy Basketball Team</span>.
                Different jerseys, different arenas &mdash; but every one of them ran on the same
                fundamentals: <span className="text-navy font-semibold">trust, communication,
                accountability,</span> and a mission bigger than any one player.
              </p>
              <p>
                Those fundamentals didn&rsquo;t stay on the court. Across government, higher
                education, nonprofit management, and public service, Marlon has led by returning to
                the same principle: a leader&rsquo;s job is to bring people together around a
                common purpose and make sure every member of the team can contribute to the
                outcome.
              </p>
              <p>
                <span className="text-navy font-semibold">WHO was built from that belief.</span>{" "}
                Just as championship teams win through coordinated effort, veterans and their
                families deserve a coordinated team working on their behalf &mdash; one that
                communicates, collaborates, and stays accountable until the outcome is real.
              </p>
            </div>

            <aside
              className="lg:col-span-4 relative rounded-xl p-7 lg:p-8 bg-navy-deep text-cream border border-gold/40 self-start"
              data-testid="teamwork-pullquote"
            >
              <div className="absolute inset-0 pointer-events-none" aria-hidden>
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gold/12 blur-2xl" />
              </div>
              <div className="relative">
                <span className="font-display font-black text-gold text-6xl leading-none select-none" aria-hidden>
                  &ldquo;
                </span>
                <p className="mt-2 font-display font-bold text-cream text-lg lg:text-xl leading-[1.35]">
                  The greatest victories are never individual. They&rsquo;re what happens when{" "}
                  <span className="text-gold">the right team decides to move together</span>.
                </p>
                <p className="mt-6 text-xs uppercase tracking-[0.22em] text-cream/60 font-semibold">
                  &mdash; Marlon L. Ferguson
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Condensed Manifesto */}
      <section
        className="relative py-24 lg:py-28 bg-cream border-t border-borderSubtle overflow-hidden"
        data-testid="founder-manifesto-section"
        id="leadership-manifesto"
      >
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-gold/8 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-navy/[0.04] blur-3xl" />
        </div>

        <div className="relative max-w-[820px] mx-auto px-6 lg:px-10">
          <p className="eyebrow text-gold-deep" data-testid="manifesto-eyebrow">
            A note from the founder
          </p>
          <h2
            className="mt-4 font-display font-black text-navy tracking-tight leading-[1.0] text-4xl sm:text-5xl"
            data-testid="manifesto-title"
          >
            Leave it better than<br />
            you found it.
          </h2>

          <div className="mt-8 flex items-center gap-4">
            <span className="h-[2px] w-14 bg-gold" />
            <p className="text-sm uppercase tracking-[0.22em] text-navy/60 font-semibold">
              Marlon L. Ferguson · Founder & CEO
            </p>
          </div>

          <div className="mt-10 space-y-6 text-navy/90 text-lg leading-[1.75]" data-testid="manifesto-copy">
            <p className="pl-6 border-l-4 border-gold font-display font-bold text-navy text-xl leading-tight italic">
              Leadership isn&rsquo;t a title or a speech. It&rsquo;s the willingness to act.
            </p>
            <p>
              Across government, higher education, veteran services, and nonprofit leadership,
              I have met organizations full of talented, caring people who already understood
              the problems in front of them. The challenge was rarely awareness — it was moving
              from recognition to action.
            </p>
            <p>
              Problems get discussed but not resolved. Silos get acknowledged but preserved.
              People get referred without anyone remaining accountable for what happens next.
              Those experiences inspired <span className="font-semibold text-navy">Warm Hand Off</span>.
            </p>
            <p className="text-navy font-medium">
              A referral is not a connection. Good intentions are not measurable outcomes.
              WHO exists to build the infrastructure that lets organizations coordinate — and
              stay accountable — around the person who needs help.
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-borderSubtle flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="font-display font-black text-3xl lg:text-4xl text-gold-deep leading-none">
                If not you, then WHO.
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.22em] text-navy/50 font-semibold">
                — Marlon L. Ferguson
              </p>
            </div>
            <img
              src="/assets/founder-marlon-ceo-square.webp"
              alt="Marlon L. Ferguson, Founder and Chief Executive Officer of Warm Hand Off LLC."
              width="480"
              height="480"
              className="w-24 h-24 rounded-full object-cover border-2 border-gold/50 shadow-md hidden sm:block"
              aria-hidden
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Foundation */}
      <Section eyebrow="Foundation" title="The belief WHO was built on." bg="bone" id="foundation">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { t: "Lived experience", b: "Built by a veteran, for veterans, families, and the communities that serve them." },
            { t: "Professional practice", b: "Shaped by years of nonprofit leadership and public-sector coordination." },
            { t: "Systems accountability", b: "Designed to make organizations coordinate around the person — not the other way around." },
          ].map((p) => (
            <div key={p.t} className="p-7 bg-white border border-borderSubtle rounded-lg" data-testid={`foundation-${p.t.toLowerCase().replace(/\W+/g, "-")}`}>
              <h3 className="font-display font-bold text-navy-900 text-lg">{p.t}</h3>
              <p className="mt-3 text-slateSoft leading-relaxed">{p.b}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 p-10 lg:p-14 bg-navy-900 text-white rounded-lg" data-testid="founder-closing">
          <p className="eyebrow text-gold">Closing statement</p>
          <p className="mt-4 font-display font-black text-3xl lg:text-4xl leading-[1.1]">
            WHO was built on a simple belief:<br />
            when someone needs help,<br />
            the handoff should be <span className="text-gold-light">warm, accountable, and human</span>.
          </p>
          <p className="mt-6 eyebrow text-gold">If not you, then WHO.</p>
        </div>
      </Section>

      <CtaBanner
        eyebrow="Join the mission."
        title="Help build the coordination infrastructure your community deserves."
        primaryText="Become a Partner"
        primaryTo="/partners"
        secondaryText="Meet WHO Guides"
        secondaryTo="/platform"
      />
    </div>
  );
}

function Chip({ icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-white border border-borderSubtle rounded-md text-navy-900 text-sm font-medium" data-testid={`founder-chip-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <span className="text-gold">{icon}</span>
      {label}
    </div>
  );
}
